<template>
    <div class="price-tag-editor">
        <div class="price-tag-editor__tag">
            <price-tag-renderer
                :layout="priceTag.layout"
                :values="modelValue"
                :fields-by-key="fieldsByKey"
                :errors="errors"
                :context="context"
                editable
                @update-value="updateValue"
            />
        </div>
        <div v-if="restrictionFields.length" class="price-tag-editor__restrictions">
            <div class="price-tag-editor__restrictions-title">
                {{ toSentenceCase($t(`${PRICE_TAGS_I18N_BASE}.restrictions`)) }}
            </div>
            <template v-for="field in restrictionFields" :key="field.key">
                <v-checkbox
                    v-if="field.type === priceTagFieldTypes.boolean"
                    :model-value="!!modelValue[field.key]"
                    :label="fieldLabel(field.key)"
                    density="compact"
                    hide-details
                    @update:model-value="value => updateValue({ key: field.key, value })"
                />
                <v-text-field
                    v-else
                    :model-value="modelValue[field.key]"
                    :label="fieldLabel(field.key)"
                    type="number"
                    :min="field.min"
                    density="compact"
                    variant="outlined"
                    clearable
                    hide-details
                    class="price-tag-editor__number"
                    @update:model-value="
                        value => updateValue({ key: field.key, value: toNumber(value) })
                    "
                />
            </template>
        </div>
        <ul v-if="errorMessages.length" class="price-tag-editor__errors">
            <li v-for="message in errorMessages" :key="message">{{ message }}</li>
        </ul>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { isNil } from 'lodash';
import priceTagFieldTypes from '@enums/price-tag-field-types';
import priceTagFieldSections from '@enums/price-tag-field-sections';
import i18n from '@/js/vue-i18n';
import { toSentenceCase } from '@/js/utils/string-utils';
import { PRICE_TAGS_I18N_BASE } from '@/js/composables/usePriceTagText';
import { applyValuesToMechanic } from '@/js/utils/price-tags/binding-engine';
import { buildPriceTagContext } from '@/js/utils/price-tags/price-tag-context';
import { validatePriceTagValues, keyFields } from '@/js/utils/price-tags/validate-values';

const props = defineProps({
    priceTag: { type: Object, required: true },
    offerMechanic: { type: Object, required: true },
    modelValue: { type: Object, default: () => ({}) },
    displayValues: { type: Object, default: () => ({}) },
    contextSource: { type: Object, required: true },
});
const emit = defineEmits(['update:modelValue']);
const fields = computed(() => props.priceTag.fields || []);
const fieldsByKey = computed(() => keyFields(fields.value));
const restrictionFields = computed(() =>
    fields.value.filter(field => field.section === priceTagFieldSections.restrictions)
);
const errors = computed(() => validatePriceTagValues(fields.value, props.modelValue));
const errorMessages = computed(() =>
    Object.entries(errors.value).map(([key, { rule, params }]) =>
        toSentenceCase(
            i18n.global.t(`${PRICE_TAGS_I18N_BASE}.validations.${rule}`, {
                field: i18n.global.t(`${PRICE_TAGS_I18N_BASE}.fields.${key}`),
                ...params,
            })
        )
    )
);
const fieldLabel = key => toSentenceCase(i18n.global.t(`${PRICE_TAGS_I18N_BASE}.fields.${key}`));
const toNumber = value => {
    if (isNil(value) || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};
const updateValue = ({ key, value }) =>
    emit('update:modelValue', { ...props.modelValue, [key]: value });
const context = computed(() => {
    const { offerMechanic, displayValues } = applyValuesToMechanic({
        template: props.offerMechanic,
        fields: fields.value,
        values: props.modelValue,
        transformContext: props.contextSource.transformContext,
    });
    return buildPriceTagContext({
        ...props.contextSource,
        offerMechanic,
        displayValues,
        useStoredPrices: false,
    });
});
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.price-tag-editor {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    padding: 1.6rem 0;

    &__tag {
        display: flex;
        justify-content: center;
    }

    &__restrictions {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 1.6rem;
    }

    &__restrictions-title {
        width: 100%;
        font-weight: 600;
    }

    &__number {
        max-width: 20rem;
    }

    &__errors {
        color: $promo-error;
        margin: 0;
    }
}
</style>
